import { useAppSelector } from 'app/store';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import { useMemo } from 'react';

export enum AccountType {
  Agave = 'Agave',
  ErpConnection = 'ErpConnection',
  Procore = 'Procore',
  FlexCode = 'FlexCode',
  Default = 'Default',
}

export const useAccountType = () => {
  const { selectedPaidol } = useAppSelector(selectUserCompanies);

  // Strict: only true for paidols on the legacy Agave integration. UI-rendering
  // gates that should treat ErpConnection paidols the same way must use
  // hasErpCodingUI; Agave-API gates (agaveAccountToken, posting) stay strict.
  const isAgaveClient = useMemo(
    () => selectedPaidol?.isAgaveClient ?? false,
    [selectedPaidol?.isAgaveClient]
  );

  // Paidol has completed the new direct-ERP connection flow (non-Agave).
  // Acts like Agave from the coding UI's perspective — data comes from the
  // same AgaveGlCode / AgaveVendor / AgaveProject / AgaveCostCode tables,
  // populated by our erp-conn-sync task.
  const isErpConnectionClient = useMemo(
    () => !!(selectedPaidol?.erpSpeedchain && selectedPaidol?.erpConnectionId),
    [selectedPaidol?.erpSpeedchain, selectedPaidol?.erpConnectionId]
  );

  // Convenience flag for UI-rendering decisions where either integration
  // should produce the same coding experience (dropdowns, columns, etc.).
  // Keep Agave-specific API calls (agaveAccountToken, posting) gated on
  // isAgaveClient alone — those paths are not yet wired for ErpConnection.
  const hasErpCodingUI = useMemo(
    () => isAgaveClient || isErpConnectionClient,
    [isAgaveClient, isErpConnectionClient]
  );

  const isProcoreClient = useMemo(
    () => selectedPaidol?.isProcoreClient ?? false,
    [selectedPaidol?.isProcoreClient]
  );

  const isConstructionType = useMemo(
    () => selectedPaidol?.isConstructionType ?? false,
    [selectedPaidol?.isConstructionType]
  );

  const isFlexCodeClient = useMemo(
    () => selectedPaidol?.flexCodingSettings?.isEnabled ?? false,
    [selectedPaidol?.flexCodingSettings?.isEnabled]
  );

  const isFlexCodingEnabled = useMemo(() => {
    const isEnabled =
      (selectedPaidol?.flexCodingSettings?.isEnabled && !isAgaveClient && !isErpConnectionClient) ?? false;
    return isEnabled;
  }, [selectedPaidol?.flexCodingSettings?.isEnabled, isAgaveClient, isErpConnectionClient]);

  const accountType = useMemo(() => {
    // Check ErpConnection before Agave so a paidol that's completed the
    // direct-ERP flow is reported as ErpConnection. A "real" Agave paidol
    // (isAgaveClient=true) still lands on Agave.
    if (isErpConnectionClient) return AccountType.ErpConnection;
    if (isAgaveClient) return AccountType.Agave;
    if (isProcoreClient) return AccountType.Procore;
    if (isFlexCodeClient) return AccountType.FlexCode;
    return AccountType.Default;
  }, [isAgaveClient, isErpConnectionClient, isProcoreClient, isFlexCodeClient]);

  return {
    isAgaveClient,
    isErpConnectionClient,
    hasErpCodingUI,
    isProcoreClient,
    isConstructionType,
    isFlexCodeClient,
    isFlexCodingEnabled,
    accountType,
  };
};
